import React, { useState } from 'react';
import { Star, ShoppingBag, Swords, Shield, Brain, Heart } from 'lucide-react';
import { PurchaseModal } from '../../modals/PurchaseModal';
import { PurchaseSuccessModal } from '../../modals/PurchaseSuccessModal';
import { usePurchase } from '../../../hooks/usePurchase';
import { getRarityColor } from '../../../utils/itemUtils';
import type { Item } from '../../../types/items';
import type { User } from '../../../types/user';

interface ItemCardProps {
  item: Item;
  user: User | null;
  onPurchaseComplete?: () => void;
}

export function ItemCard({ item, user, onPurchaseComplete }: ItemCardProps) {
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { purchaseItem, isProcessing, error, clearError } = usePurchase(user);
  const disabled = !user || user.points < item.price;

  const handlePurchase = async () => {
    const success = await purchaseItem(item);
    if (success) {
      setShowPurchaseModal(false);
      setShowSuccessModal(true);
      onPurchaseComplete?.();
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
  };

  return (
    <>
      <div 
        className={`bg-gradient-to-br from-purple-500 to-pink-500 p-1 rounded-xl transform transition-all 
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'}`}
        onClick={() => !disabled && setShowPurchaseModal(true)}
      >
        <div className="bg-gray-900 p-6 rounded-lg h-full">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white">{item.name}</h3>
            <Star className={`w-5 h-5 ${getRarityColor(item.rarity)}`} />
          </div>

          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-full h-48 object-cover rounded-lg mb-4"
          />

          <div className="space-y-4">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-400 capitalize">{item.type}</span>
              <div className="flex items-center gap-1 text-yellow-400">
                <ShoppingBag className="w-4 h-4" />
                <span className="font-mono">{item.price.toLocaleString()} coins</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {item.stats.strength && (
                <div className="flex items-center gap-1 text-sm">
                  <Swords className="w-4 h-4 text-red-400" />
                  <span className="text-red-400">STR: {item.stats.strength}</span>
                </div>
              )}
              {item.stats.agility && (
                <div className="flex items-center gap-1 text-sm">
                  <Shield className="w-4 h-4 text-green-400" />
                  <span className="text-green-400">AGI: {item.stats.agility}</span>
                </div>
              )}
              {item.stats.intelligence && (
                <div className="flex items-center gap-1 text-sm">
                  <Brain className="w-4 h-4 text-blue-400" />
                  <span className="text-blue-400">INT: {item.stats.intelligence}</span>
                </div>
              )}
              {item.stats.vitality && (
                <div className="flex items-center gap-1 text-sm">
                  <Heart className="w-4 h-4 text-pink-400" />
                  <span className="text-pink-400">VIT: {item.stats.vitality}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showPurchaseModal && (
        <PurchaseModal
          item={item}
          userPoints={user?.points || 0}
          onConfirm={handlePurchase}
          onCancel={() => {
            setShowPurchaseModal(false);
            clearError();
          }}
          isProcessing={isProcessing}
          error={error}
        />
      )}

      {showSuccessModal && (
        <PurchaseSuccessModal
          item={item}
          onClose={handleSuccessClose}
        />
      )}
    </>
  );
}